import { useEffect, useRef, useState } from 'react';
import { useLazyGetVerificationByIdQuery as useLazyGetWidgetVerificationIdQuery } from '@modules/docs-verification-widget/api';

import type { FinishStepProps } from '../types';

import { VERIFICATION_POLL_INTERVAL_MS } from './constants';
import { useGetZkProof } from './use-get-zk-proof';

type UseWidgetVerificationParams = {
    isWeb3?: boolean;
    isCustomPartner?: boolean;
    apiUrl?: string;
    shortId: string;
    getZkProof?: FinishStepProps['getZkProof'];
};

export const useWidgetVerification = ({
    isWeb3,
    isCustomPartner,
    apiUrl,
    shortId,
    getZkProof,
}: UseWidgetVerificationParams) => {
    const timer = useRef<ReturnType<typeof setTimeout>>();
    const [isLoading, setIsLoading] = useState(false);
    const [getVerificationByIdStatus, { data: verificationData }] =
        useLazyGetWidgetVerificationIdQuery();

    const shouldPoll = isWeb3 || isCustomPartner;

    useEffect(() => {
        if (!shouldPoll) {
            return undefined;
        }

        if (!isCustomPartner) {
            setIsLoading(true);
        }

        getVerificationByIdStatus({
            id: shortId,
            url: apiUrl as string,
        });

        timer.current = setInterval(() => {
            getVerificationByIdStatus({
                id: shortId,
                url: apiUrl as string,
            });
        }, VERIFICATION_POLL_INTERVAL_MS);

        return () => {
            if (timer.current) {
                clearInterval(timer.current);
            }
        };
    }, [apiUrl, getVerificationByIdStatus, isCustomPartner, shouldPoll, shortId]);

    useEffect(() => {
        if (verificationData?.verified != null && timer.current) {
            clearInterval(timer.current);
        }
    }, [verificationData?.verified]);

    const { zkProof, zkProofError } = useGetZkProof({
        verified: isCustomPartner ? undefined : verificationData?.verified,
        apiUrl,
        shortId,
        getZkProof,
        timer,
        setIsLoading,
        isCustomPartner,
    });

    const verified = verificationData?.verified;

    return {
        verificationData,
        zkProofProps: {
            isWeb3,
            isLoading,
            verified,
            zkProof,
            zkProofError,
        },
    };
};
